import { DialogProps } from "@mui/material";
import { QueryProgress } from "core/types";
import { NonIconOnlyButtonProps } from "ds/ui";
import { ReactNode, useCallback, useRef, useState } from "react";

export type MultiStepDialogStep<
  TStepDataMap extends StepDataMap = StepDataMap,
> = {
  cancelButtonProps?: Partial<Omit<NonIconOnlyButtonProps, "onPress" | "ref">>;
  cancelText?: string;
  confirmButtonProps?: Partial<Omit<NonIconOnlyButtonProps, "onPress">>;
  content?: ReactNode;
  data?: TStepDataMap;
  headerIcon?: ReactNode;
  id: string;
  onStepCancel?: () => void;
  onStepSubmit?: (
    data?: TStepDataMap,
  ) => Promise<boolean | any> | boolean | any;
  showCancel?: boolean;
  showSubmit?: boolean;
  submitText?: string;
  sx?: DialogProps["sx"];
  title?: ReactNode | string;
};

export type MultiStepConfirmationDialogProps = {
  cancelButtonProps?: Partial<Omit<NonIconOnlyButtonProps, "onPress" | "ref">>;
  cancelText?: string;
  confirmButtonProps?: Partial<Omit<NonIconOnlyButtonProps, "onPress" | "ref">>;
  content?: ReactNode;
  currentStepIndex: number;
  headerIcon?: ReactNode;
  id: string;
  isLoading: boolean;
  loadingMessage?: string;
  onCancel: () => void;
  onSubmit: (data?: any) => void;
  opened: boolean;
  queryProgress?: QueryProgress;
  showCancel?: boolean;
  showSubmit?: boolean;
  submitText?: string;
  sx?: DialogProps["sx"];
  title: ReactNode | string;
  totalSteps: number;
  withProgress?: boolean;
};

export type StepDataMap = Record<string, any>;

// Helper type to create step data types
export type CreateStepDataMap<T extends Record<string, any>> = T;

export type MultiStepConfirmationResult<
  TStepDataMap extends StepDataMap = StepDataMap,
> = {
  confirmed: boolean;
  data?: TStepDataMap;
  stepData?: TStepDataMap;
};

/**
 * A multi-step confirmation dialog hook that supports loading states and data loading.
 *
 * Features:
 * - Multiple steps with individual data loading
 * - Loading states with custom messages
 * - Step validation and data collection
 * - Progress tracking
 * - Async step processing
 */
export function useMultiStepConfirmationDialog<
  TStepDataMap extends StepDataMap = StepDataMap,
>() {
  const [props, setProps] = useState<MultiStepConfirmationDialogProps>({
    currentStepIndex: 0,
    id: "multi-step-confirmation",
    isLoading: false,
    onCancel: () => {},
    onSubmit: () => {},
    opened: false,
    title: "",
    totalSteps: 0,
  });

  const [steps, setSteps] = useState<MultiStepDialogStep<TStepDataMap>[]>([]);
  const [stepData, setStepData] = useState<TStepDataMap>({} as TStepDataMap);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStepIndexRef = useRef(0);
  const stepDataRef = useRef<TStepDataMap>({} as TStepDataMap);

  const updateDialogProps = useCallback(
    (newProps: Partial<MultiStepConfirmationDialogProps>) => {
      setProps((current) => ({ ...current, ...newProps }));
    },
    [],
  );

  const setLoading = useCallback(
    (isLoading: boolean, message?: string) => {
      updateDialogProps({ isLoading, loadingMessage: message });
    },
    [updateDialogProps],
  );

  const goToStep = useCallback(
    (
      stepIndex: number,
      dialogStepsOverride?: MultiStepDialogStep<TStepDataMap>[],
    ) => {
      const stepsToUse = dialogStepsOverride || steps;
      if (stepIndex >= 0 && stepIndex < stepsToUse.length) {
        setCurrentStepIndex(stepIndex);
        currentStepIndexRef.current = stepIndex;
        const step = stepsToUse[stepIndex];

        // Check if we have step data from previous step that should be used for content
        let finalComponentContent = step.content;
        let finalTitle = step.title || "";
        let finalCancelText = step.cancelText;
        let finalSubmitText = step.submitText;
        let finalCancelButtonProps = step.cancelButtonProps;
        let finalConfirmButtonProps = step.confirmButtonProps;
        let finalHeaderIcon = step.headerIcon;

        if (stepIndex === 1 && stepDataRef.current["consent"]?.content) {
          // This is step 1 (activation), use the content from step 0 (consent)
          const sourceData = stepDataRef.current["consent"];
          finalComponentContent = sourceData.content;
          finalTitle = sourceData.title || step.title || "";

          // Direct property overrides take precedence
          if (sourceData.cancelButtonProps !== undefined) {
            finalCancelButtonProps = sourceData.cancelButtonProps;
          }
          if (sourceData.cancelText !== undefined) {
            finalCancelText = sourceData.cancelText;
          }
          if (sourceData.confirmButtonProps !== undefined) {
            finalConfirmButtonProps = sourceData.confirmButtonProps;
          }
          if (sourceData.headerIcon !== undefined) {
            finalHeaderIcon = sourceData.headerIcon;
          }
          if (sourceData.submitText !== undefined) {
            finalSubmitText = sourceData.submitText;
          }
        }

        updateDialogProps({
          cancelButtonProps: finalCancelButtonProps,
          cancelText: finalCancelText,
          confirmButtonProps: finalConfirmButtonProps,
          content: finalComponentContent,
          currentStepIndex: stepIndex,
          headerIcon: finalHeaderIcon,
          isLoading: false, // Clear loading state when transitioning to a step
          loadingMessage: undefined, // Clear loading message
          showCancel: step.showCancel !== false,
          showSubmit: step.showSubmit !== false,
          submitText: finalSubmitText,
          sx: step.sx,
          title: finalTitle,
          totalSteps: stepsToUse.length,
        });

        // Auto-execute onStepSubmit for steps that have no buttons
        if (step.onStepSubmit && step.showSubmit === false) {
          setTimeout(async () => {
            try {
              setLoading(true, "Processing...");
              const result = await step.onStepSubmit!();

              if (
                result &&
                typeof result === "object" &&
                result.hasRightsForAll !== undefined
              ) {
                // This is step 1, move to step 2 with the data
                // Store the step data first
                const updatedStepData = {
                  ...stepDataRef.current,
                  [stepsToUse[stepIndex].id]: result,
                };
                stepDataRef.current = updatedStepData;
                setStepData(updatedStepData);
                // Then move to next step
                goToStep(stepIndex + 1, stepsToUse);
              }

              setLoading(false);
            } catch (error) {
              console.error("Auto-executed onStepSubmit failed:", error);
              setLoading(false);
            }
          }, 100);
        }
      }
    },
    [steps, updateDialogProps],
  );

  const nextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      goToStep(currentStepIndex + 1);
    }
  }, [currentStepIndex, steps.length, goToStep]);

  const previousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      goToStep(currentStepIndex - 1);
    }
  }, [currentStepIndex, goToStep]);

  const confirmMultiStep = useCallback(
    async (
      dialogSteps: MultiStepDialogStep<TStepDataMap>[],
      initialData?: TStepDataMap,
    ): Promise<MultiStepConfirmationResult<TStepDataMap>> => {
      return new Promise((resolve) => {
        setSteps(dialogSteps);
        setStepData((initialData || {}) as TStepDataMap);
        stepDataRef.current = (initialData || {}) as TStepDataMap;
        setCurrentStepIndex(0);
        currentStepIndexRef.current = 0;

        const firstStep = dialogSteps[0];
        if (!firstStep) {
          resolve({ confirmed: false });
          return;
        }

        setProps({
          cancelButtonProps: firstStep.cancelButtonProps,
          cancelText: firstStep.cancelText,
          confirmButtonProps: firstStep.confirmButtonProps,
          content: firstStep.content,
          currentStepIndex: 0,
          headerIcon: firstStep.headerIcon,
          id: "multi-step-confirmation",
          isLoading: true,
          onCancel: () => {
            resolve({ confirmed: false });
            setProps((current) => ({ ...current, opened: false }));
          },
          onSubmit: async (data?: any) => {
            // Get the current step index from the ref
            const currentStepIndex = currentStepIndexRef.current;
            const currentStep = dialogSteps[currentStepIndex];

            // Handle step-specific submit logic
            let stepResult: any = undefined;
            if (currentStep.onStepSubmit) {
              try {
                // Don't set loading here - let the step's onStepSubmit handle its own loading state
                // Pass the accumulated step data instead of just the current step's data
                const accumulatedData = {
                  ...stepDataRef.current,
                  [currentStep.id]: data,
                };
                stepResult = await currentStep.onStepSubmit(accumulatedData);

                if (!stepResult) {
                  setLoading(false);
                  return; // Don't proceed to next step
                }
              } catch (error) {
                console.error("Step processing failed:", error);
                setLoading(false);
                return; // Don't proceed on error
              }
            }

            // Store step data (use stepResult if available, otherwise use data)
            const dataToStore = stepResult || data;
            if (dataToStore) {
              const updatedStepData = {
                ...stepDataRef.current,
                [currentStep.id]: dataToStore,
              };
              stepDataRef.current = updatedStepData;
              setStepData(updatedStepData);
            }

            // Don't automatically clear loading state - let the step's onStepSubmit handle it
            // setLoading(false);

            // Check if this is the last step
            if (currentStepIndex === dialogSteps.length - 1) {
              resolve({
                confirmed: true,
                data: data,
                stepData: { ...stepData, [currentStep.id]: data },
              });
              setProps((current) => ({ ...current, opened: false }));
            } else {
              // Move to next step
              // Use dialogSteps instead of steps state to avoid closure issues
              goToStep(currentStepIndex + 1, dialogSteps);
            }
          },
          opened: true,
          showCancel: firstStep.showCancel !== false,
          showSubmit: firstStep.showSubmit !== false,
          submitText: firstStep.submitText,
          sx: firstStep.sx,
          title: firstStep.title || "",
          totalSteps: dialogSteps.length,
        });

        // Show initial loading state briefly, then transition to first step
        setTimeout(() => {
          goToStep(0, dialogSteps);
        }, 300);

        // Auto-execute onStepSubmit for steps that have no buttons
        if (firstStep.onStepSubmit && firstStep.showSubmit === false) {
          setTimeout(() => {
            firstStep.onStepSubmit!();
          }, 100);
        }
      });
    },
    [currentStepIndex, nextStep, setLoading, stepData],
  );

  return {
    confirmMultiStep,
    currentStepIndex,
    goToStep,
    nextStep,
    previousStep,
    props,
    setLoading,
    stepData,
    totalSteps: steps.length,
  } as const;
}
